import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Clock, ShieldCheck, XCircle, FileText, Plus, ArrowRight,
         ClipboardCheck, PackageCheck, Truck, Link2, Search, X, ExternalLink } from "lucide-react";
import { catColor, catEmoji } from "@/pages/portal/PortalOrder";

const STATUS_MAP = {
    submitted:        { label: "New",              icon: Clock,       cls: "bg-amber-100 text-amber-800" },
    confirmed:        { label: "Confirmed",        icon: ShieldCheck, cls: "bg-sky-100 text-sky-800" },
    packing:          { label: "Packing",          icon: PackageCheck,cls: "bg-violet-100 text-violet-800" },
    out_for_delivery: { label: "Out for Delivery", icon: Truck,       cls: "bg-orange-100 text-orange-800" },
    delivered:        { label: "Delivered",        icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-800" },
    converted:        { label: "Invoiced",         icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-800" },
    cancelled:        { label: "Cancelled",        icon: XCircle,     cls: "bg-red-100 text-red-800" },
};

export default function ClientOrdersPage() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("submitted");
    const [linkModal, setLinkModal] = useState(null);   // order awaiting invoice-link
    const nav = useNavigate();

    const load = () => api.get("/admin/client-orders", { params: { status: filter } })
        .then((r) => setRows(r.data));
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const convert = async (o) => {
        // Route to NewInvoice with the client + line items pre-filled.
        // CRITICAL: refetch the order from the server — the local `rows`
        // copy may be stale (e.g. custom items approved seconds ago live in
        // DB line_items but not in this render).  A stale prefill is how
        // approved items used to go missing from the invoice.
        try {
            const r = await api.get(`/admin/client-orders/${o.id}`);
            const fresh = r.data;
            sessionStorage.setItem("hdb-prefill-order", JSON.stringify({
                client_order_id: fresh.id,
                name: fresh.client_name,
                invoice_type: fresh.invoice_type || "B2B",
                line_items: fresh.line_items || [],
                reorder_of_invoice_id: fresh.reorder_of_invoice_id || "",
                notes: fresh.notes || "",
            }));
            nav(`/invoices/new?fromClientOrder=${fresh.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load the order — try again");
        }
    };

    const markConverted = async (o) => {
        if (!window.confirm("Mark this order as converted (already created invoice manually)?")) return;
        try {
            await api.post(`/admin/client-orders/${o.id}/mark-converted`, { invoice_id: "" });
            toast.success("Marked as converted");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const setStatus = async (o, newStatus) => {
        try {
            await api.put(`/admin/client-orders/${o.id}/status`, { status: newStatus });
            toast.success(`Marked ${STATUS_MAP[newStatus]?.label || newStatus}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="client-orders-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Client Orders</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Orders submitted by clients via the portal — convert each into an invoice
                    </p>
                </div>
                <div className="flex gap-2">
                    {["submitted", "converted", "cancelled", ""].map((f) => (
                        <button key={f || "all"} onClick={() => setFilter(f)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
                                    filter === f ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"
                                }`}
                                data-testid={`client-orders-filter-${f || "all"}`}>
                            {f === "" ? "All" : STATUS_MAP[f]?.label || f}
                        </button>
                    ))}
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="hdb-card p-12 text-center" data-testid="client-orders-empty">
                    <Inbox size={40} className="mx-auto text-slate-300 mb-3"/>
                    <div className="text-slate-500">
                        {filter === "submitted"
                            ? "No new client orders — you're all caught up."
                            : "No orders in this state."}
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {rows.map((o) => {
                        const s = STATUS_MAP[o.status] || STATUS_MAP.submitted;
                        const Icon = s.icon;
                        return (
                            <details key={o.id} className="hdb-card" data-testid={`client-order-${o.id}`}>
                                <summary className="p-4 cursor-pointer flex items-center gap-4 flex-wrap">
                                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${s.cls}`}>
                                        <Icon size={11}/> {s.label}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900">{o.client_name}</div>
                                        <div className="text-xs text-slate-500">
                                            {o.created_at?.slice(0,10)} · {o.line_items.length} items
                                            {o.delivery_date_preference && ` · wants delivery ${o.delivery_date_preference}`}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-black font-mono text-slate-900">{fmt(o.grand_total)}</div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-wider">Estimated</div>
                                    </div>
                                    {o.status === "submitted" && (
                                        <div className="flex gap-2 ml-auto sm:ml-0 flex-wrap">
                                            {o.custom_items?.length > 0 && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide bg-amber-100 text-amber-900 border border-amber-300 rounded-full px-2 py-1"
                                                      data-testid={`client-order-pending-badge-${o.id}`}>
                                                    ⚠ {o.custom_items.length} awaiting review
                                                </span>
                                            )}
                                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); convert(o); }}
                                                    disabled={o.custom_items?.length > 0}
                                                    title={o.custom_items?.length > 0
                                                        ? `Review ${o.custom_items.length} custom item${o.custom_items.length !== 1 ? "s" : ""} first`
                                                        : "Create invoice from this order"}
                                                    className="hdb-btn hdb-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                                                    data-testid={`client-order-convert-${o.id}`}>
                                                <Plus size={11}/> Create invoice <ArrowRight size={11}/>
                                            </button>
                                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); markConverted(o); }}
                                                    className="hdb-btn hdb-btn-ghost text-xs"
                                                    data-testid={`client-order-mark-${o.id}`}>
                                                <ClipboardCheck size={11}/> Mark done
                                            </button>
                                        </div>
                                    )}
                                    {/* Link-to-invoice retrofit is available on ANY non-cancelled order
                                        (including already-converted ones — useful for Cambrian Hall's
                                        HBEPL/26-27/0059 where the order needs to be re-synced with
                                        the invoice's real items + delivery status). */}
                                    {o.status !== "cancelled" && (
                                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLinkModal(o); }}
                                                className="hdb-btn text-xs bg-indigo-600 text-white hover:bg-indigo-700 ml-auto sm:ml-2"
                                                data-testid={`client-order-link-${o.id}`}>
                                            <Link2 size={11}/> {o.converted_invoice_number ? "Re-sync with invoice" : "Link to existing invoice"}
                                        </button>
                                    )}
                                    {o.converted_invoice_number && (
                                        <div className="w-full mt-1 flex items-center gap-2 flex-wrap"
                                             data-testid={`client-order-linked-${o.id}`}>
                                            <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded uppercase">
                                                Invoiced · {o.converted_invoice_number}
                                            </span>
                                            {o.converted_invoice_id && (
                                                <a href={`/invoices/${o.converted_invoice_id}`}
                                                   onClick={(e) => e.stopPropagation()}
                                                   className="text-[10px] font-bold text-white bg-slate-900 hover:bg-slate-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                                                    Open <ExternalLink size={8}/>
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </summary>
                                <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                                    {/* Status progression buttons — admin drives the tracker */}
                                    {o.status !== "converted" && o.status !== "cancelled" && (
                                        <div className="my-2 flex flex-wrap items-center gap-1.5"
                                             data-testid={`status-controls-${o.id}`}>
                                            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mr-1">Mark as:</span>
                                            {["confirmed", "packing", "out_for_delivery", "delivered"].map(s => (
                                                <button key={s} onClick={() => setStatus(o, s)}
                                                        disabled={o.status === s}
                                                        className={`text-xs font-bold px-2.5 py-1 rounded-lg border transition-colors
                                                                    ${o.status === s ? "bg-slate-900 text-white border-slate-900"
                                                                                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
                                                        data-testid={`set-status-${s}-${o.id}`}>
                                                    {STATUS_MAP[s].label}
                                                </button>
                                            ))}
                                            <button onClick={() => setStatus(o, "cancelled")}
                                                    className="text-xs font-bold px-2.5 py-1 rounded-lg border border-red-300 bg-white text-red-700 hover:bg-red-50 ml-auto">
                                                Cancel order
                                            </button>
                                        </div>
                                    )}

                                    {/* Custom items requested by the client — need admin confirmation of HSN/GST/price.
                                        key forces a remount whenever the server's custom_items array
                                        changes size so the panel can never act on stale indices. */}
                                    {o.custom_items?.length > 0 && (
                                        <CustomItemsAdminPanel key={`${o.id}-${o.custom_items.length}`}
                                                               orderId={o.id}
                                                               customItems={o.custom_items}
                                                               onSaved={load} />
                                    )}

                                    {/* Line items — grouped by category (mirrors invoice/PO format) */}
                                    {o.reorder_of_invoice_number && (
                                        <div className="mt-2 text-xs font-bold text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 inline-block"
                                             data-testid={`admin-order-reorder-of-${o.id}`}>
                                            Reorder of invoice {o.reorder_of_invoice_number} — rates locked to that invoice; ↑/↓ shows today's item-master rate
                                        </div>
                                    )}
                                    <CategorizedOrderLines lines={o.line_items} keepOrder={!!o.reorder_of_invoice_id} />
                                    {o.notes && (
                                        <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-lg p-2 text-slate-700">
                                            <b>Notes from client:</b> {o.notes}
                                        </div>
                                    )}
                                </div>
                            </details>
                        );
                    })}
                </div>
            )}
            {linkModal && (
                <LinkInvoiceModal order={linkModal}
                                  onClose={() => setLinkModal(null)}
                                  onLinked={() => { setLinkModal(null); load(); }} />
            )}
        </div>
    );
}

/**
 * CustomItemsAdminPanel — admin reviews items the client typed that aren't
 * in the master catalog. For each: click "AI suggest" to prefill HSN/GST/
 * category, edit as needed, then "Approve → add to items" writes into the
 * items master collection so next time it's a normal catalog item.
 */
function CustomItemsAdminPanel({ orderId, customItems, onSaved }) {
    // Rows are keyed by the custom item's stable `id` (stamped by the backend
    // at order submit; legacy orders get ids backfilled by GET single-order).
    // Approvals are ID-based — never index-based — because every approval
    // shrinks the server-side array and position indices silently shift.
    const [rows, setRows] = useState(customItems.map(ci => ({
        ...ci, hsn: "", tax_percent: 0, category: "Uncategorised",
        b2b_price: 0, b2c_price: 0,
        classifySource: "",     // 'fuzzy' | 'ai' | 'unknown' — origin of the current suggestion
        saveMaster: true,        // "Save as new master item" — opt out on 1-off items
        saving: false,
    })));
    const [categories, setCategories] = useState([]);
    // Snapshot of what the classifier suggested per row, so we can detect
    // when the admin's final values diverge (→ HSN correction learning).
    const classifiedRef = useRef({});

    const setRow = (rowId, patch) =>
        setRows((cur) => cur.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
    const removeRow = (rowId) => setRows((cur) => cur.filter((r) => r.id !== rowId));

    // Load existing categories for the dropdown + auto-classify each custom row on mount.
    useEffect(() => {
        api.get("/items/categories").then((r) => setCategories(r.data || [])).catch(() => {});
        customItems.forEach((ci) => {
            api.get("/admin/classify-item", { params: { name: ci.name } })
               .then((r) => {
                    if (r.data.source === "unknown") return;
                    classifiedRef.current[ci.id] = {
                        hsn: r.data.hsn || "",
                        tax_percent: r.data.tax_percent || 0,
                        category: r.data.category || "Uncategorised",
                    };
                    setRow(ci.id, {
                        hsn: r.data.hsn || "",
                        tax_percent: r.data.tax_percent || 0,
                        category: r.data.category || "Uncategorised",
                        classifySource: r.data.source,
                        aiReason: r.data.source === "learned"
                            ? `Learned from previous correction`
                            : r.data.source === "fuzzy"
                            ? `Fuzzy-matched "${r.data.match_name}" (${r.data.score}%)`
                            : `AI-classified`,
                    });
                }).catch(() => {});
        });
        // eslint-disable-next-line
    }, []);

    const suggest = async (rowId) => {
        const row = rows.find((r) => r.id === rowId);
        if (!row) return;
        setRow(rowId, { suggesting: true });
        try {
            const r = await api.post("/items/ai-suggest",
                { name: row.name, unit: row.unit });
            setRow(rowId, {
                hsn: r.data.hsn || "",
                tax_percent: r.data.tax_percent || 0,
                category: r.data.category || "Uncategorised",
                aiReason: r.data.reason,
                suggesting: false,
            });
            toast.success(`AI suggested HSN ${r.data.hsn} · GST ${r.data.tax_percent}%`);
        } catch (e) {
            setRow(rowId, { suggesting: false });
            toast.error(e.response?.data?.detail || "AI suggestion failed");
        }
    };

    const approve = async (rowId) => {
        const r = rows.find((x) => x.id === rowId);
        if (!r) return;
        if (!r.name.trim()) return toast.error("Item name required");
        setRow(rowId, { saving: true });
        try {
            const resp = await api.post(`/admin/client-orders/${orderId}/approve-custom`, {
                custom_id: r.id || "",        // stable id — backend resolves by id
                name: r.name.trim(),
                hsn: r.hsn || "",
                category: r.category || "Uncategorised",
                unit: r.unit || "Pack",
                tax_percent: r.tax_percent || 0,
                b2b_price: r.b2b_price || 0,
                b2c_price: r.b2c_price || r.b2b_price || 0,
                invoice_type: "B2B",
            });
            if (!r.saveMaster && resp.data.item_id) {
                api.delete(`/items/${resp.data.item_id}`).catch(() => {});
            }
            // HSN correction learning: if staff's final values differ from
            // the classifier's suggestion, persist name→(hsn,gst,category)
            // so the next occurrence auto-fills correctly with no AI cost.
            const suggested = classifiedRef.current[rowId];
            const diverged = !suggested || suggested.hsn !== r.hsn
                          || suggested.tax_percent !== r.tax_percent
                          || suggested.category !== r.category;
            if (diverged && (r.hsn || r.tax_percent || r.category)) {
                api.post("/admin/classify-item/learn", {
                    name: r.name.trim(), hsn: r.hsn || "",
                    tax_percent: r.tax_percent || 0,
                    category: r.category || "Uncategorised",
                }).catch(() => {});
            }
            removeRow(rowId);
            toast.success(r.saveMaster
                ? `Added "${r.name}" to master · moved into ${r.category}`
                : `Moved "${r.name}" into this order · not saved to master`);
            onSaved && onSaved();
        } catch (e) {
            setRow(rowId, { saving: false });
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const approveAll = async () => {
        // Batch-approve — sequential so backend order is deterministic and
        // per-row failures surface as toasts without aborting the rest.
        const pending = rows.filter((r) => !r.saving && r.name.trim());
        if (!pending.length) return;
        toast.info(`Approving ${pending.length} item${pending.length > 1 ? "s" : ""}…`);
        for (const r of pending) {
            // eslint-disable-next-line no-await-in-loop
            await approve(r.id);
        }
    };

    return (
        <div className="my-2 rounded-lg border-2 border-amber-300 bg-amber-50/40 overflow-hidden"
             data-testid={`custom-items-panel-${orderId}`}>
            <div className="px-3 py-2 bg-amber-500 text-white flex items-center gap-2">
                <span className="text-base">✍️</span>
                <span className="font-black text-xs uppercase tracking-wide">
                    {rows.length} custom item{rows.length !== 1 ? "s" : ""} — need admin confirmation
                </span>
                <button onClick={approveAll}
                        disabled={rows.every((r) => r.saving) || rows.length === 0}
                        className="ml-auto text-[10px] font-black uppercase tracking-wide bg-white text-amber-700 hover:bg-amber-50 disabled:opacity-50 px-2.5 py-1 rounded"
                        title="Approve every row with its current HSN/GST/category values — great for large orders"
                        data-testid={`custom-approve-all-${orderId}`}>
                    ✓ Approve all
                </button>
            </div>
            <table className="w-full text-xs">
                <thead className="text-slate-500 text-[10px] uppercase tracking-wider bg-white/60">
                    <tr>
                        <th className="text-left px-2 py-1.5">Client wrote</th>
                        <th className="text-left px-2 py-1.5">Qty · Unit</th>
                        <th className="text-left px-2 py-1.5">HSN</th>
                        <th className="text-right px-2 py-1.5">GST %</th>
                        <th className="text-left px-2 py-1.5">Category</th>
                        <th className="text-right px-2 py-1.5">B2B ₹</th>
                        <th className="text-right px-2 py-1.5">B2C ₹</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-amber-200/50">
                    {rows.map((r, i) => (
                        <tr key={r.id || i} data-testid={`custom-admin-row-${i}`}>
                            <td className="px-2 py-1.5">
                                <div className="font-semibold text-slate-900">{r.name}</div>
                                {r.notes && <div className="text-[10px] text-slate-500 italic">"{r.notes}"</div>}
                                {r.aiReason && <div className="text-[9px] text-blue-600 mt-0.5">AI: {r.aiReason}</div>}
                            </td>
                            <td className="px-2 py-1.5 font-mono">{r.qty} {r.unit}</td>
                            <td className="px-2 py-1.5">
                                <input value={r.hsn} onChange={(e) => setRow(r.id, { hsn: e.target.value })}
                                       className="w-20 px-1.5 py-0.5 border border-slate-200 rounded font-mono"
                                       placeholder="HSN" data-testid={`custom-hsn-${i}`} />
                            </td>
                            <td className="px-2 py-1.5 text-right">
                                <input type="number" value={r.tax_percent}
                                       onChange={(e) => setRow(r.id, { tax_percent: parseFloat(e.target.value) || 0 })}
                                       className="w-14 text-right px-1.5 py-0.5 border border-slate-200 rounded font-mono"
                                       data-testid={`custom-gst-${i}`} />
                            </td>
                            <td className="px-2 py-1.5">
                                <select value={categories.includes(r.category) ? r.category : "__custom__"}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === "__custom__") setRow(r.id, { category: r.category || "" });
                                            else setRow(r.id, { category: v });
                                        }}
                                        className="w-32 px-1.5 py-0.5 border border-slate-200 rounded bg-white"
                                        data-testid={`custom-cat-select-${i}`}>
                                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                    <option value="__custom__">+ New category…</option>
                                </select>
                                {!categories.includes(r.category) && (
                                    <input value={r.category} onChange={(e) => setRow(r.id, { category: e.target.value })}
                                           placeholder="New category name"
                                           className="mt-1 w-32 px-1.5 py-0.5 border border-slate-200 rounded"
                                           data-testid={`custom-cat-input-${i}`}/>
                                )}
                                {r.classifySource && (
                                    <div className={`text-[9px] mt-0.5 font-bold uppercase tracking-wide ${r.classifySource === "fuzzy" ? "text-emerald-700" : "text-blue-700"}`}>
                                        {r.classifySource === "fuzzy" ? "✓ Auto-matched" : "✨ AI-detected"}
                                    </div>
                                )}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                                <input type="number" value={r.b2b_price}
                                       onChange={(e) => setRow(r.id, { b2b_price: parseFloat(e.target.value) || 0 })}
                                       className="w-20 text-right px-1.5 py-0.5 border border-slate-200 rounded font-mono" />
                            </td>
                            <td className="px-2 py-1.5 text-right">
                                <input type="number" value={r.b2c_price}
                                       onChange={(e) => setRow(r.id, { b2c_price: parseFloat(e.target.value) || 0 })}
                                       className="w-20 text-right px-1.5 py-0.5 border border-slate-200 rounded font-mono" />
                            </td>
                            <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                <div className="flex flex-col items-end gap-1">
                                    <label className="text-[9px] text-slate-600 flex items-center gap-1 cursor-pointer select-none"
                                           title="Uncheck to only add this to the current order without saving as a permanent master item">
                                        <input type="checkbox" checked={r.saveMaster}
                                               onChange={(e) => setRow(r.id, { saveMaster: e.target.checked })}
                                               className="w-3 h-3 accent-emerald-600"
                                               data-testid={`custom-savemaster-${i}`}/>
                                        Save to master
                                    </label>
                                    <div className="flex gap-1">
                                        <button onClick={() => suggest(r.id)} disabled={r.suggesting}
                                                className="text-[10px] font-bold px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                                data-testid={`custom-suggest-${i}`}>
                                            {r.suggesting ? "…" : "AI"}
                                        </button>
                                        <button onClick={() => approve(r.id)} disabled={r.saving}
                                                className="text-[10px] font-bold px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                                                data-testid={`custom-approve-${i}`}>
                                            {r.saving ? "…" : "Approve"}
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}



/* Groups the order's line_items by category with a colored band header —
   mirrors the invoice PDF layout so admin + client see the same shape. */
const PriceMove = ({ l }) => {
    if (l.master_rate === 0) return <span className="ml-1 text-[10px] font-bold text-amber-700" data-testid="admin-order-price-unpriced">not priced now</span>;
    if (l.master_rate == null || Math.abs(l.master_rate - (l.rate || 0)) < 0.005) return null;
    const up = l.master_rate > (l.rate || 0);
    return (
        <span className={`ml-1 font-bold ${up ? "text-red-600" : "text-emerald-700"}`}
              title={`Item master now ${fmt(l.master_rate)}`} data-testid={`admin-order-price-${up ? "up" : "down"}`}>
            {up ? "↑" : "↓"} {fmt(l.master_rate)}
        </span>
    );
};

function CategorizedOrderLines({ lines, keepOrder = false }) {
    if (!lines || lines.length === 0) {
        return <div className="text-center text-slate-400 text-xs py-4 mt-2">No line items yet.</div>;
    }
    const buckets = {};
    const displayName = {};
    for (const l of lines) {
        const raw = (l.category || "Uncategorised").trim();
        const key = raw.toLowerCase();
        (buckets[key] ||= []).push(l);
        displayName[key] = displayName[key] || raw;
    }
    const entries = keepOrder ? Object.entries(buckets) : Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
    return (
        <div className="mt-2 bg-white rounded-lg border border-slate-200 overflow-hidden"
             data-testid="admin-order-lines-grouped">
            {entries.map(([k, catLines]) => {
                const name = displayName[k];
                const col = catColor(name);
                const catTotal = catLines.reduce((s, l) => s + (l.qty || 0) * (l.rate || 0), 0);
                return (
                    <div key={k} className="border-b border-slate-100 last:border-b-0"
                         data-testid={`admin-order-cat-${name}`}>
                        <div className={`${col.bg} ${col.text} px-3 py-1.5 flex items-center gap-2 text-xs font-black uppercase tracking-wide border-l-4 ${col.border.replace("border-", "border-l-")}`}>
                            <span>{catEmoji(name)}</span>
                            <span>{name}</span>
                            <span className="opacity-70 font-mono">· {catLines.length}</span>
                            <span className="ml-auto font-mono">{fmt(catTotal)}</span>
                        </div>
                        <table className="w-full text-sm table-fixed">
                            <colgroup><col/><col className="w-28"/><col className="w-36"/><col className="w-28"/></colgroup>
                            <tbody className="divide-y divide-slate-100">
                                {catLines.map((l, j) => (
                                    <tr key={j} className={l.was_custom ? "bg-emerald-50/40" : ""}>
                                        <td className="py-1.5 px-3">
                                            {l.name}
                                            {l.was_custom && <span className="ml-2 text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded uppercase">NEW→APPROVED</span>}
                                        </td>
                                        <td className="py-1.5 text-right font-mono text-xs">{l.qty} {l.unit}</td>
                                        <td className="py-1.5 text-right font-mono text-xs text-slate-500 whitespace-nowrap">{fmt(l.rate)}<PriceMove l={l}/></td>
                                        <td className="py-1.5 text-right font-mono font-bold text-xs pr-3">{fmt(l.line_total || (l.qty || 0) * (l.rate || 0))}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </div>
    );
}


/* Backfill / retro-link modal — used when the client's order was already
   fulfilled by an existing invoice (e.g. before this feature shipped).
   Search by invoice number, pick, done.  Optionally jump the order status
   forward (e.g. "delivered") in one shot. */
function LinkInvoiceModal({ order, onClose, onLinked }) {
    const [q, setQ] = useState(order.client_name || "");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [alsoStatus, setAlsoStatus] = useState("delivered");
    const [replaceLines, setReplaceLines] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const t = setTimeout(async () => {
            if (!q.trim()) { setResults([]); return; }
            try {
                const r = await api.get("/invoices", { params: { q, limit: 30 } });
                setResults(r.data);
            } catch (e) { /* noop */ }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const link = async () => {
        if (!selected) return toast.error("Pick an invoice first");
        setSaving(true);
        try {
            const r = await api.post(`/admin/client-orders/${order.id}/link-invoice`, {
                invoice_id: selected.id,
                also_set_status: alsoStatus,
                replace_order_lines: replaceLines,
            });
            const rep = r.data.replaced_order_lines
                ? ` · order items replaced with ${r.data.linked_invoice.line_count} invoice items`
                : "";
            toast.success(`Linked to ${selected.invoice_number} · marked ${alsoStatus}${rep}`);
            onLinked && onLinked();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Link failed");
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
             onClick={onClose} data-testid="link-invoice-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                 onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b border-slate-200 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-700 grid place-items-center">
                        <Link2 size={18}/>
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-black hdb-heading">Link this order to an existing invoice</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {order.client_name} · {order.line_items?.length || 0} items · ₹{fmt(order.grand_total || 0)}
                        </p>
                        <p className="text-[11px] text-indigo-700 mt-1 font-semibold">
                            One-time retrofit: the order's items + total will be replaced with the invoice's real contents so the client sees exactly what was billed.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18}/></button>
                </div>
                <div className="p-4 border-b border-slate-200">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-2.5 text-slate-400"/>
                        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                               className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                               placeholder="Search by invoice number, buyer, GSTIN…"
                               data-testid="link-invoice-search"/>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
                    {results.length === 0 && (
                        <div className="text-center text-slate-400 text-sm py-6">
                            {q ? "No invoices match" : "Start typing to search"}
                        </div>
                    )}
                    {results.map(inv => {
                        const isSel = selected?.id === inv.id;
                        return (
                            <button key={inv.id} onClick={() => setSelected(inv)}
                                    className={`w-full text-left p-2.5 rounded-lg border ${isSel ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200" : "border-slate-200 hover:bg-slate-50"}`}
                                    data-testid={`link-invoice-result-${inv.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs font-bold">{inv.invoice_number}</span>
                                    <span className="text-xs text-slate-600">{inv.invoice_date}</span>
                                    <span className="ml-auto font-mono text-sm font-bold">{fmt(inv.grand_total)}</span>
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5 truncate">
                                    {inv.buyer?.name} {inv.buyer?.gstin ? `· ${inv.buyer.gstin}` : ""} · {(inv.line_items || []).length} items
                                </div>
                            </button>
                        );
                    })}
                </div>
                <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-3">
                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={replaceLines}
                               onChange={(e) => setReplaceLines(e.target.checked)}
                               className="w-4 h-4 accent-indigo-600"
                               data-testid="link-invoice-replace-lines"/>
                        <span className="text-slate-700">
                            <b>Replace order items with invoice items</b> — client will see the exact invoiced line items + total (recommended)
                        </span>
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Also mark:</label>
                            <select value={alsoStatus} onChange={(e) => setAlsoStatus(e.target.value)}
                                    className="text-sm px-2 py-1 border border-slate-300 rounded bg-white"
                                    data-testid="link-invoice-status">
                                <option value="converted">Invoiced (no delivery status)</option>
                                <option value="delivered">Delivered</option>
                                <option value="out_for_delivery">Out for delivery</option>
                                <option value="packing">Packing</option>
                                <option value="confirmed">Confirmed</option>
                            </select>
                        </div>
                        <button onClick={link} disabled={!selected || saving}
                                className={`hdb-btn hdb-btn-primary ml-auto ${!selected || saving ? "opacity-40 cursor-not-allowed" : ""}`}
                                data-testid="link-invoice-submit">
                            {saving ? "Linking…" : "Link & set status"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
